<script lang="ts" setup>
import { useAdvisor } from '#advisor-portal/state/useAdvisor'
import { ConsultationSourceEnum } from '@forgd/contract/types'
import { Badge } from '@forgd/ui-kit/badge'
import { ProjectAvatar } from '@forgd/ui-kit/blocks/avatars'

const props = withDefaults(defineProps<Options>(), { containerClass: 'max-w-[1200px]' })

interface Options {
  title?: string
  icon?: string
  cta?: string
  ctaBtnText?: string
  contact?: boolean
  containerClass?: false | string
  containerLayout?: 'center' | 'fluid'
  collapsedNav?: boolean
  appFooterClass?: string
  pageHeader?: PageHeaderMeta
}

const auth = useAuth()
const advisor = useAdvisor()
const searchTerm = ref('')
const { searchGroups } = useAppSearch(searchTerm)
const { isContentSearchModalOpen } = useUIState()

const options = computed<Options>(() => {
  return { ...props, ...useRoute().meta }
})

interface PageHeaderMeta {
  description?: string
  title?: string
  icon?: string
}
const pageHeader = computed(() => (useRoute().meta as { pageHeader?: PageHeaderMeta }).pageHeader ?? props.pageHeader)
</script>

<template>
  <div>
    <NuxtLayout name="core-dashboard" v-bind="props">
      <template #nav>
        <AppNav />
      </template>
      <template #mobile-header>
        <CorePageHeaderMobile />
      </template>
      <template #page-header>
        <CorePageHeader
          :title="pageHeader?.title"
          :icon="pageHeader?.icon"
          :description="pageHeader?.description"
        >
          <template #title-prefix>
            <slot name="page-header-prefix" />
          </template>
          <template #title-suffix>
            <slot name="page-header-suffix" />
            <Badge v-if="auth.isAdvisor && !advisor.isInPortal && advisor.project" variant="gray" size="md" class="gap-1">
              <ProjectAvatar :src="advisor.project.image" size="2xs" />
              <span>{{ advisor.project.name }}</span>
            </Badge>
          </template>
          <template #actions>
            <ProjectEditControl v-if="advisor.hasProjectEditControl" />
          </template>
          <template v-if="$slots['header-auth']" #auth>
            <slot name="header-auth" />
          </template>
        </CorePageHeader>
      </template>
      <template v-if="$slots.sidebar" #sidebar>
        <slot name="sidebar" />
      </template>
      <template v-if="$slots.subheader" #subheader>
        <slot name="subheader" />
      </template>
      <template v-if="$slots['right-sidebar']" #right-sidebar>
        <slot name="right-sidebar" />
      </template>
      <template #header>
        <div class="flex items-center gap-2">
          <slot name="header">
            <div class="flex items-center gap-2">
              <img v-if="options.icon && options.icon.endsWith('.svg')" :src="options.icon" class="h-8">
              <div class="shrink-0 text-sm font-semibold">
                {{ options.title }}
              </div>
            </div>
          </slot>
          <Badge v-if="auth.isAdvisor && !advisor.isInPortal" variant="gray" size="md" class="gap-1">
            <ProjectAvatar :src="advisor.project?.image" size="2xs" />
            <span>{{ advisor.project?.name }}</span>
          </Badge>
        </div>
      </template>
      <template #header-actions>
        <ProjectEditControl v-if="advisor.hasProjectEditControl" />
      </template>
      <template v-if="$slots['header-auth']" #header-auth>
        <slot name="header-auth" />
      </template>
      <template #header-notifications>
        <ClientOnly>
          <CoreAppNotifications v-if="auth.isLoggedIn" />
        </ClientOnly>
      </template>
      <template #footer>
        <slot name="footer" />
        <CoreSupportCta
          v-if="options.contact"
          :cta="options.cta"
          :cta-btn-text="options.ctaBtnText"
          :source="ConsultationSourceEnum.DashboardFooterCta"
        />
      </template>
      <template v-if="$slots['app-footer']" #app-footer>
        <slot name="app-footer" />
      </template>
      <slot />
    </NuxtLayout>
    <SwitchAccountProfileModal />
    <ForbiddenTeammateActionModal />
    <AuthLoginModal />
    <AuthSelectProjectModal />
    <ClientOnly>
      <LazyCoreSearchModal v-model="searchTerm" v-model:is-open="isContentSearchModalOpen" :groups="searchGroups" />
    </ClientOnly>
  </div>
</template>
